import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCode } from 'react-qr-code';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFetch } from '@/hooks/use-fetch';

const QUERY_KEY = ['two-factor'];

/**
 * AIT-750 — opt-in authenticator-app two-factor, shown as a row in Edit
 * profile. Turning it on: scan a QR code, type the first code. Turning it
 * off needs a current code. Nothing changes at sign-in until the first code
 * verifies (the backend only stores the factor then).
 */
export function TwoFactorSetting() {
  const fetchWithAuth = useFetch();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'enable' | 'disable' | null>(null);

  const { data, isPending } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<{ enabled: boolean } | null> => {
      try {
        const res = await fetchWithAuth('/auth/two-factor', undefined, { suppressGlobalError: true });
        if (!res.ok) return null;
        return (await res.json()) as { enabled: boolean };
      } catch {
        return null;
      }
    },
  });

  const done = (message: string) => {
    setDialog(null);
    toast.success(message);
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  };

  return (
    <div className="space-y-1">
      <Label>Two-factor login</Label>
      <div className="flex items-center justify-between gap-2">
        {isPending ? (
          <span className="text-sm text-muted-foreground">Loading…</span>
        ) : !data ? (
          <span className="text-sm text-muted-foreground">Could not load. Try again later.</span>
        ) : data.enabled ? (
          <>
            <span className="text-sm">On</span>
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('disable')}>
              Turn off
            </Button>
          </>
        ) : (
          <>
            <span className="text-sm text-muted-foreground">Ask for a code from your phone when you log in.</span>
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('enable')}>
              Turn on
            </Button>
          </>
        )}
      </div>
      <Dialog open={dialog !== null} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          {dialog === 'enable' && <EnableForm onDone={() => done('Two-factor login is on')} onCancel={() => setDialog(null)} />}
          {dialog === 'disable' && <DisableForm onDone={() => done('Two-factor login is off')} onCancel={() => setDialog(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CodeField({ id, code, setCode, error }: { id: string; code: string; setCode: (v: string) => void; error: string | null }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>6-digit code</Label>
      <Input
        id={id}
        placeholder="123456"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

/** POSTs JSON and returns the backend's userMessage on failure, null on success. */
function usePostCode() {
  const fetchWithAuth = useFetch();
  return async (path: string, body: object): Promise<string | null> => {
    try {
      const res = await fetchWithAuth(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, { suppressGlobalError: true });
      if (res.ok) return null;
      const json = (await res.json().catch(() => ({}))) as { message?: string };
      return json.message ?? 'Something went wrong. Try again.';
    } catch {
      return 'Something went wrong. Try again.';
    }
  };
}

function EnableForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const postCode = usePostCode();
  const fetchWithAuth = useFetch();
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchWithAuth('/auth/two-factor/setup', { method: 'POST' }, { suppressGlobalError: true });
        if (cancelled) return;
        if (res.ok) setSetup((await res.json()) as { secret: string; uri: string });
        else setError('Could not start setup. Close this and try again.');
      } catch {
        if (!cancelled) setError('Could not start setup. Close this and try again.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchWithAuth]);

  const submit = async () => {
    if (!setup) return;
    setBusy(true);
    setError(null);
    const err = await postCode('/auth/two-factor/enable', { secret: setup.secret, code });
    setBusy(false);
    if (err) setError(err);
    else onDone();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Turn on two-factor login</DialogTitle>
        <DialogDescription>
          Scan this with an authenticator app like Google Authenticator or 1Password, then type the code it shows.
        </DialogDescription>
      </DialogHeader>
      {setup ? (
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className="rounded-md bg-white p-3">
              <QRCode value={setup.uri} size={160} aria-label="Two-factor setup QR code" />
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            Can't scan? Enter this key in the app: <span className="font-mono break-all text-foreground">{setup.secret}</span>
          </p>
          <CodeField id="two-factor-enable-code" code={code} setCode={setCode} error={error} />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <div className="flex justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" aria-label="Loading" />
        </div>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !setup || code.length !== 6}>
          {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Turn on
        </Button>
      </DialogFooter>
    </>
  );
}

function DisableForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const postCode = usePostCode();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    const err = await postCode('/auth/two-factor/disable', { code });
    setBusy(false);
    if (err) setError(err);
    else onDone();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Turn off two-factor login?</DialogTitle>
        <DialogDescription>
          Type the current code from your authenticator app. After this, you log in with just your password.
        </DialogDescription>
      </DialogHeader>
      <CodeField id="two-factor-disable-code" code={code} setCode={setCode} error={error} />
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Keep it on</Button>
        <Button variant="destructive" onClick={submit} disabled={busy || code.length !== 6}>
          {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Turn off
        </Button>
      </DialogFooter>
    </>
  );
}
